import {
  isDone,
  isDueLater,
  isDueThisWeek,
  isDueToday,
  isDueTomorrow,
  isOverdue,
  isRecentlyDone,
} from "../tasks/tasksPredicate";

/**
 * Which tasks each of the three task surfaces shows.
 *
 * THREE SURFACES, THREE DIFFERENT JOBS, AND THEY MUST NOT COLLAPSE
 * ---------------------------------------------------------------
 * The owner asked for all three in one sentence, and the distinction is the
 * whole request:
 *
 *   1. the CONTACT PAGE panel   what is outstanding on this person, right now
 *   2. /contacts/:id/tasks      everything ever asked of this person
 *   3. /tasks                   my own work queue, open and completed
 *
 * (1) is a LESS request, not a more one: "at the current contact detail page,
 * I dont need to see everything -- just the currently open tasks". Growing it
 * into a history is the failure mode this file exists to prevent, which is why
 * the panel links to (2) rather than gaining a "show closed" control.
 *
 * WHY THE PANEL IS NOT SIMPLY "OPEN TASKS"
 * ----------------------------------------
 * Two reasons, and neither is decoration.
 *
 * The five-minute grace: upstream keeps a task on screen for five minutes
 * after it is ticked (tasks/tasksPredicate.ts) so a misclick does not make the
 * row vanish before it can be undone. The owner asked for that explicitly --
 * "so a misclick doesnt require a bunch of extra clicks to undo" -- so a panel
 * that filtered on `done_date is null` alone would be a regression dressed as
 * a simplification.
 *
 * The empty case: a contact with nothing open used to render a bare "no tasks"
 * placeholder, which reads identically whether nobody has ever done anything
 * for this person or somebody closed three tasks this morning. Showing the
 * last couple of closed ones answers "did anything happen here?" without
 * turning the panel into the log that surface 2 already is.
 *
 * THE RULES ARE PURE FUNCTIONS OVER ROWS, ON PURPOSE. Asserting "a task closed
 * ten minutes ago is absent" through a rendered list costs a browser and ~1.5s
 * every run; here it is one Node assertion at ~1ms, and the browser tests are
 * left to prove the wiring only. See marketing/CLAUDE.md.
 */

/**
 * How many closed tasks the contact panel falls back to.
 *
 * "A couple", which is the owner's word and therefore theirs to change -- so
 * it is a named constant rather than a `.slice(0, 2)` somewhere in a render.
 * Two is enough to say what just happened without the panel becoming the
 * history it deliberately is not; the whole history is one click away at
 * contactTasksPath().
 */
export const CLOSED_TASKS_ON_CONTACT = 2;

/**
 * How many closed rows the panel FETCHES, which is not the same number.
 *
 * The five-minute grace needs every task ticked in the last five minutes, and
 * somebody clearing a backlog can tick more than two of them in that window.
 * Fetching only CLOSED_TASKS_ON_CONTACT would make the third row a person
 * ticked disappear instantly -- the exact misclick trap the grace exists to
 * close, reintroduced by the fallback that was supposed to help.
 *
 * Ten, because the read is `done_date desc limit 10` on a table holding 69
 * rows: the cost is nothing and the ceiling is well past any real burst.
 */
export const CLOSED_TASKS_FETCHED = 10;

/** What the panel is currently showing, so the caller can say so in words. */
export type ContactPanelMode =
  /** Open work (and anything ticked in the last five minutes). */
  | "open"
  /** Nothing open, so the last couple of closed ones stand in. */
  | "fallback"
  /** Nothing at all, ever. */
  | "empty";

type TaskLike = { due_date: string; done_date?: string | null };

/**
 * Upstream's predicates want `done_date: string | null`; the `Task` type
 * declares it OPTIONAL, so a row that has never been closed can arrive as
 * `undefined` rather than as null. `undefined` is not "done" either, so the
 * two collapse here rather than at four call sites -- and collapsing them at
 * the call sites is how one of the four ends up spelled `!= null` and one
 * `!== null`, which differ on exactly this value.
 */
const predicateShape = (task: TaskLike) => ({
  due_date: task.due_date,
  done_date: task.done_date ?? null,
});

/**
 * The rows the contact panel shows, and which of the three states it is in.
 *
 * `closed` MUST arrive newest-first by `done_date` -- the caller reads it that
 * way from the database (`sort: done_date DESC`), and this function does not
 * re-sort it. Handing it an unsorted array yields "a couple of closed tasks"
 * that are not the last couple, which is a wrong answer that looks right.
 *
 * The recently-done rows come out of `closed` rather than out of `open`,
 * because `open` is now read with a `done_date is null` predicate and cannot
 * contain them by construction. That predicate is the point: it is what lets
 * the partial index `tasks_open_by_contact` (crm#27, migration 089) serve the
 * read at all.
 */
export function contactPanelTasks<T extends TaskLike>(
  open: readonly T[] | undefined,
  closed: readonly T[] | undefined,
): { rows: T[]; mode: ContactPanelMode } {
  const live = [
    ...(open ?? []),
    // Ticked within the last five minutes. Still on screen, still untickable.
    ...(closed ?? []).filter((task) => isRecentlyDone(predicateShape(task))),
  ];

  if (live.length) return { rows: live, mode: "open" };

  const recent = (closed ?? []).slice(0, CLOSED_TASKS_ON_CONTACT);
  return recent.length
    ? { rows: recent, mode: "fallback" }
    : { rows: [], mode: "empty" };
}

/**
 * Open and completed, split.
 *
 * NO FIVE-MINUTE GRACE HERE, and that is the difference between surface 1 and
 * surface 2. On the panel the grace stops a row vanishing under a misclick; on
 * a page that shows both halves at once, a just-ticked task counted as open
 * would sit in the Open list saying "done" -- the page would disagree with
 * itself about the one fact it exists to report.
 */
export function partitionTasks<T extends TaskLike>(
  tasks: readonly T[] | undefined,
): { open: T[]; completed: T[] } {
  const open: T[] = [];
  const completed: T[] = [];
  for (const task of tasks ?? [])
    (isDone(predicateShape(task)) ? completed : open).push(task);
  return { open, completed };
}

/**
 * The two views of the global task queue, in the order they are shown.
 *
 * Open first: it is the work. Completed is the audit trail, and until this
 * change it did not exist anywhere in the app -- a task closed more than five
 * minutes ago was invisible on every surface that renders one (atomic#15).
 */
export const TASK_VIEWS = ["open", "completed"] as const;

export type TaskView = (typeof TASK_VIEWS)[number];

/**
 * Namespaced per screen, as `data-table.tsx` does with `storeKey` and as
 * `followups.queue` and `lifecycle.picker.q` already are.
 *
 * The store is shared by every screen in the app and keyed by nothing but this
 * string, so a bare "view" would be claimed by whichever screen mounted last
 * and both would then read a value the other wrote.
 *
 * Which view is showing is a CHOICE THE USER MADE, not a dialog flag: someone
 * reviewing what was actually done opens a contact from the list, comes back,
 * and with `useState` lands on Open again every single time. That is the
 * papercut atomic#13 names. The stored value is checked before use by
 * `queueToShow` in followUpsRules.ts -- see the long note there on why a
 * persisted value is a SUGGESTION rather than state.
 */
export const TASKS_VIEW_KEY = "tasks.view";

/**
 * The due-date sections, in the order they are shown.
 *
 * A SECOND ARRANGEMENT OF UPSTREAM'S PREDICATES, KNOWINGLY, AND NOT A SECOND
 * DEFINITION OF THEM. `tasks/TasksListByDueDate.tsx` builds the same five
 * groups inline with five `useMemo`s; this is that grouping as data, so the
 * contact panel can render the same sections over rows it read itself rather
 * than over rows that component queried. Every predicate below is imported
 * from `tasks/tasksPredicate.ts` -- there is still exactly one definition of
 * "overdue" in the app, and drift can only ever be in which sections exist,
 * which is visible on screen rather than silent.
 *
 * The alternative was giving the panel no sections at all. That would have
 * regressed the mobile contact tab, where they exist today, to save this
 * array -- and "overdue" is the one thing a task list must not stop saying.
 *
 * `thisWeekBeforeFridayOnly` is upstream's own rule, carried across rather
 * than rediscovered: on a contact panel the "this week" group is hidden from
 * Friday onward, because by then it holds one or two days and reads as an
 * empty heading. The global list shows it every day.
 */
export const DUE_SECTIONS = [
  {
    key: "overdue",
    titleKey: "resources.tasks.filters.overdue",
    match: (dueDate: string) => isOverdue(dueDate),
  },
  {
    key: "today",
    titleKey: "resources.tasks.filters.today",
    match: (dueDate: string) => isDueToday(dueDate),
  },
  {
    key: "tomorrow",
    titleKey: "resources.tasks.filters.tomorrow",
    match: (dueDate: string) => isDueTomorrow(dueDate),
  },
  {
    key: "this_week",
    titleKey: "resources.tasks.filters.this_week",
    match: (dueDate: string) => isDueThisWeek(dueDate),
    thisWeekBeforeFridayOnly: true,
  },
  {
    key: "later",
    titleKey: "resources.tasks.filters.later",
    match: (dueDate: string) => isDueLater(dueDate),
  },
] as const;

/**
 * Split rows into the five sections above.
 *
 * EVERY ROW LANDS SOMEWHERE, and that is what the test asserts rather than
 * spot-checking one date. The five predicates tile the timeline, so a row
 * falling through all of them is a row that silently disappears from a panel
 * whose entire job is to say what is outstanding -- the failure mode a
 * per-section assertion cannot see.
 */
export function bucketByDueDate<T extends TaskLike>(
  tasks: readonly T[],
): Record<string, T[]> {
  const sections: Record<string, T[]> = {};
  for (const { key } of DUE_SECTIONS) sections[key] = [];
  for (const task of tasks) {
    const section = DUE_SECTIONS.find(({ match }) => match(task.due_date));
    if (section) sections[section.key].push(task);
  }
  return sections;
}
